import { useEffect, useState } from "react";
import useAction from "../AccountMode/useAction";
import { Typography } from "@blofin/blofin-ui";

import { useStoreState } from "../store/useStore";
import { useTranslations } from "use-intl";
import { numberFormat, transformUSDT } from "../utils/index";
import useWatchLoaclstorageValue from "./useWatchLoaclstorageValue";
import { textWeight } from "@/nav-localized/config";
import clsx from "clsx";
import {
  assetPopperCardHover,
  assetPopperGroupHoverLabel,
  assetPopperQuickActionHover,
  dropDownStylesDesText,
  megaMenuDismissButton,
  stylesText
} from "../styles";

import DepositFillIcon from "@/nav-localized/assets/header/deposit-fill.svg?react";
import WithdrawFillIcon from "@/nav-localized/assets/header/withdraw-fill.svg?react";
import TransferFillIcon from "@/nav-localized/assets/header/transfer-fill.svg?react";

const defaultRate = "USD";

interface FiatList {
  name: string;
  precision: string;
  rate: string;
  symbol: string;
}

const defaultCrypto = {
  usdt_usd: "1",
  name: "USD",
  precision: "0.01",
  rate: "1",
  symbol: "$"
};

const Card = ({ accountType, visible }: { visible: boolean; accountType: string | undefined }) => {
  const { getUserAsset, getExchangeRate } = useAction();
  const [userAsset, setUserAsset] = useState("0");
  const [loading, setLoading] = useState(true);
  const [isShow, setIsShow] = useState(true);
  const [crypto, setCrypto] = useState(defaultCrypto);

  const t = useTranslations("header");
  const { localeLink, theme } = useStoreState();

  const { isDemoTrading } = useStoreState();

  const { updateLocalStorage, value: localstorageVisibility } = useWatchLoaclstorageValue({
    key: "assets-isHideTotalAssets",
    initValue: isShow ? "1" : "0"
  });

  const initAccountInfo = async () => {
    try {
      const res = await getUserAsset();
      const { code, msg, data } = res as unknown as {
        code: number;
        msg: string;
        data: { total_equity: string };
      };
      setLoading(false);
      if (code !== 200) {
        throw new Error(msg || "Get Account Mode Error");
      }
      setUserAsset(data.total_equity);
    } catch (error) {
      console.error("Get Account Mode Error", error);
    }
  };

  const getRate = async () => {
    try {
      const res = await getExchangeRate<{
        code: number;
        data: { fiat: FiatList[]; usdt_usd: string };
      }>();
      const { code, data } = res || {};
      if (code == 200) {
        const { usdt_usd = "", fiat = [] } = data || {};
        const USDInfo = fiat.find((item) => item.name === defaultRate) || defaultCrypto;
        setCrypto({
          usdt_usd,
          ...USDInfo
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const changeShow = () => {
    const newValue = !isShow;
    setIsShow(newValue);
    updateLocalStorage(newValue ? "1" : "0");
  };

  useEffect(() => {
    const newValue = localstorageVisibility === "0" ? false : true;
    setIsShow(newValue);
  }, [localstorageVisibility]);

  useEffect(() => {
    if (visible) {
      getRate();
      initAccountInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const isShowButtonRange = accountType === "MAIN";

  if (isDemoTrading) return <div className="pt-[24px]"></div>;

  const mode = theme === "dark" ? "dark" : "light";

  return (
    <div className="p-[12px]">
      <div
        className={clsx(
          "cursor-pointer group/assetcard px-[12px] py-[10px] rounded-[8px]",
          assetPopperCardHover({ mode })
        )}
        onClick={() => (window.location.href = `${localeLink}/assets/overview`)}
      >
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center">
            <Typography
              variant="body3"
              className={clsx("leading-[20px] font-medium", dropDownStylesDesText({ mode }))}
            >
              {t("overview")}
            </Typography>
            {isShow ? (
              <i
                className={clsx(
                  "ml-1 iconfont icon-show-fill text-[14px] cursor-pointer",
                  megaMenuDismissButton({ mode })
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  changeShow();
                }}
              ></i>
            ) : (
              <i
                className={clsx(
                  "ml-1 iconfont icon-hide-fill text-[14px] cursor-pointer",
                  megaMenuDismissButton({ mode })
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  changeShow();
                }}
              ></i>
            )}
          </div>
          <i
            className={clsx(
              "iconfont icon-arrow-right-l-line text-[16px]",
              assetPopperGroupHoverLabel({ mode })
            )}
          ></i>
        </div>
        <Typography
          dir="ltr"
          variant="subtitle1"
          className={`leading-[34px] ${textWeight}`}
          theme={theme}
        >
          {isShow ? `${loading ? "--" : numberFormat(userAsset, 2)}` : "******"} USDT
        </Typography>
        <Typography
          dir="ltr"
          variant="body2"
          className={clsx("leading-[24px] font-normal", dropDownStylesDesText({ mode }))}
        >
          {isShow
            ? `≈ ${loading ? "--" : transformUSDT(userAsset, Number(crypto.usdt_usd) ? Number(crypto.usdt_usd) : 1, Number(crypto.rate) ? Number(crypto.rate) : 1, crypto.precision, crypto.symbol)}`
            : "******"}
        </Typography>
      </div>
      {isShowButtonRange && (
        <div className="flex items-center gap-[16px] px-[12px] pt-[4px] pb-[4px]">
          {[
            {
              key: "deposit",
              Icon: DepositFillIcon,
              label: t("deposit"),
              onClick: () => {
                window?.reportTrack?.("Deposit_entry_click", {
                  entry_source: "asset menu deposit"
                });
                window.location.href = `${localeLink}/assets/deposit`;
              }
            },
            {
              key: "withdraw",
              Icon: WithdrawFillIcon,
              label: t("withdraw"),
              onClick: () => {
                window.location.href = `${localeLink}/assets/withdraw`;
              }
            },
            {
              key: "transfer",
              Icon: TransferFillIcon,
              label: t("transfer"),
              onClick: () => {
                window.location.href = `${localeLink}/assets/overview?transfer=true`;
              }
            }
          ].map((action) => (
            <div
              key={action.key}
              className="group/quickaction flex flex-[1_0_0] flex-col items-center justify-center gap-[4px] cursor-pointer"
              onClick={action.onClick}
            >
              <div
                className={clsx(
                  "flex items-center justify-center w-[40px] h-[40px] rounded-[8px] shrink-0",
                  mode === "dark" ? "bg-dark-fill-tertiary" : "bg-light-fill-secondary"
                )}
              >
                <action.Icon
                  className={clsx(
                    "w-[24px] h-[24px]",
                    stylesText({ mode }),
                    assetPopperQuickActionHover({ mode })
                  )}
                />
              </div>
              <p
                className={clsx(
                  "text-[12px] leading-[18px] text-center",
                  stylesText({ mode }),
                  assetPopperQuickActionHover({ mode })
                )}
              >
                {action.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Card;
